import React, { useCallback, useMemo } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useGetSpaceIdsFromSpaceKeys } from '@atlassian/company-hub-common';
import { CustomSearchMacroConfigFilters } from '@atlassian/dynamic-cards-search-page';

import type { SearchExtensionParameters } from '../../../types';

import { messages } from './messages';

export type Filters = {
	[filter: string]: any;
};

const cssStyles = cssMap({
	container: {
		// Top Styles
		paddingBlockStart: token('space.200'),
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
		borderTopStyle: 'solid',

		// Bottom styles
		paddingBlockEnd: token('space.200'),
		borderBottomWidth: token('border.width'),
		borderBottomColor: token('color.border'),
		borderBottomStyle: 'solid',
		marginBlockEnd: token('space.200'),
	},
	header: {
		paddingInline: token('space.100'),
	},
});

export type FiltersContentProps = {
	cloudId: string;
	displayName: string | null;
	filters: SearchExtensionParameters['filters'];
	isAdminHubAIEnabled: boolean;
	orgId?: string;
	setFilters: (newFilters: SearchExtensionParameters['filters']) => void;
	userId: string | null;
};

export const FiltersContent = ({
	filters,
	setFilters,
	cloudId,
	userId,
	displayName,
	orgId,
	isAdminHubAIEnabled,
}: FiltersContentProps) => {
	const getSpaceIdsFromSpaceKeys = useGetSpaceIdsFromSpaceKeys();

	const setFiltersInternal = useCallback(
		(newFilters: Filters) => {
			setFilters({ ...newFilters, product: 'confluence' });
		},
		[setFilters],
	);

	const userDetails = useMemo(() => {
		return {
			id: userId,
			name: displayName,
		};
	}, [userId, displayName]);

	const filtersToHide = useMemo(() => {
		// Only enable unified search if:
		// - User is in unified search beta and
		const isUnifiedSearchEnabled = fg('unified_search_in_confluence');

		// The "Type" filter is hidden because the current available options are a subset of
		// those on the full search page. This misalignment creats a poor user experience.
		//
		// The "Owner" filter is hidden when unified search is disabled because it is not supported by the
		// non-unified full search page.
		return isUnifiedSearchEnabled ? ['universalTypeFilter'] : ['universalTypeFilter', 'owner'];
	}, []);

	return (
		<Stack space="space.100" xcss={cssStyles.container}>
			<Box xcss={cssStyles.header}>
				<Heading size="small">
					<FormattedMessage {...messages.filtersTitle} />
				</Heading>
			</Box>
			<CustomSearchMacroConfigFilters
				shell={{
					cloudId,
					orgId,
					user: userDetails,
					primaryProduct: 'confluence' as const,
					queryParams: filters,
					isAdminHubAIEnabled,
				}}
				callbacks={{
					updateQueryParameters: setFiltersInternal,
					getSpaceIdsFromSpaceKeys,
				}}
				filtersToHide={filtersToHide}
			/>
		</Stack>
	);
};
